import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { Download, Plus, Trash2, Save } from 'lucide-react';
import axios from 'axios';
import { logError } from '../../lib/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CLASS_OPTIONS = [
  ['NONE', 'Unclassified'], ['LOCK', 'Roster Lock'], ['LIKELY_IN', 'Likely In'], ['BUBBLE', 'On the Bubble'],
  ['LONG_SHOT', 'Long Shot'], ['PROJECTED_OUT', 'Projected Out'], ['RELEASED', 'Released/Waived'], ['INJURED', 'Injured/Designation'],
  ['MADE_53', 'Made the 53'], ['PRACTICE_SQUAD', 'Practice Squad'],
];

const selectCls = 'text-xs border border-slate-300 px-1.5 py-1 bg-white max-w-full';
const inputCls = 'text-xs border border-slate-300 px-2 py-1 w-full';

const PlayerAdminRow = ({ player, positions, onSave, onDelete }) => {
  const [draft, setDraft] = useState(player);
  const dirty = JSON.stringify(draft) !== JSON.stringify(player);
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  return (
    <div data-testid={`admin-roster-row-${player.id}`} className="border border-slate-200 p-3 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-bold text-slate-900">{draft.name}</span>
        <Input value={draft.jersey || ''} onChange={(e) => set('jersey', e.target.value)} placeholder="#" className="h-7 w-14 text-xs" />
        <select value={draft.position} onChange={(e) => set('position', e.target.value)} className={selectCls}>
          {positions.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <select
          data-testid={`admin-roster-class-${player.id}`}
          value={draft.classification || 'NONE'}
          onChange={(e) => set('classification', e.target.value === 'NONE' ? null : e.target.value)}
          className={selectCls}
        >
          {CLASS_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select
          data-testid={`admin-roster-official-${player.id}`}
          value={draft.official_status || 'NONE'}
          onChange={(e) => set('official_status', e.target.value === 'NONE' ? null : e.target.value)}
          className={selectCls}
        >
          <option value="NONE">No official status</option>
          {['ACTIVE_ROSTER','SIGNED','WAIVED','RELEASED','WAIVED_INJURED','INJURED_RESERVE','PUP','NFI','RETIRED','TRADED','CLAIMED','PRACTICE_SQUAD','OTHER'].map((o) => <option key={o} value={o}>{o.replace(/_/g,' ')}</option>)}
        </select>
        <select value={draft.stock || 'NONE'} onChange={(e) => set('stock', e.target.value === 'NONE' ? null : e.target.value)} className={selectCls}>
          <option value="NONE">No stock note</option>
          <option value="UP">Stock Up ↑</option>
          <option value="DOWN">Stock Down ↓</option>
        </select>
        <label className="flex items-center gap-1 text-xs text-slate-600">
          P53:
          <select
            value={draft.p53_explicit ? (draft.in_projected_53 ? 'YES' : 'NO') : (draft.in_projected_53 ? 'YES' : 'AUTO')}
            onChange={(e) => { set('p53_override', e.target.value); set('p53_explicit', e.target.value !== 'AUTO'); set('in_projected_53', e.target.value === 'YES' ? true : e.target.value === 'NO' ? false : null); }}
            className={selectCls}
          >
            <option value="AUTO">Auto</option><option value="YES">Yes</option><option value="NO">No</option>
          </select>
        </label>
        {draft.classification === 'LOCK' && draft.p53_explicit && draft.in_projected_53 === false && (
          <span className="text-[10px] text-amber-700 font-bold">⚠ Lock excluded from 53 — save to confirm</span>
        )}
        {player.needs_review && <span className="text-[10px] bg-amber-500 text-white font-bold px-1.5 py-0.5 uppercase">Roster data needs review</span>}
        {player.review_note && <span className="text-[10px] text-amber-700 font-semibold w-full">⚠ {player.review_note}</span>}
        <label className="flex items-center gap-1 text-xs text-slate-600">
          <input type="checkbox" checked={!!draft.exempt} onChange={(e) => set('exempt', e.target.checked)} />
          Exempt
        </label>
        <div className="ml-auto flex gap-1">
          {dirty && (
            <Button size="sm" data-testid={`admin-roster-save-${player.id}`} onClick={() => onSave(player.id, draft)} className="h-7 px-2 rounded-none bg-[#002868] text-xs">
              <Save className="w-3 h-3 mr-1" /> Save
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => onDelete(player.id)} className="h-7 px-2 rounded-none text-red-700 border-red-300 text-xs">
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      </div>
      <input value={draft.note || ''} onChange={(e) => set('note', e.target.value)} placeholder="Editorial analysis note (optional)" className={inputCls} />
      {draft.stock && (
        <input value={draft.stock_note || ''} onChange={(e) => set('stock_note', e.target.value)} placeholder="Stock watch explanation" className={inputCls} />
      )}
    </div>
  );
};

export const RosterAdmin = () => {
  const [data, setData] = useState(null);
  const [posFilter, setPosFilter] = useState('ALL');
  const [classFilter, setClassFilter] = useState('ALL');
  const [selected, setSelected] = useState([]);
  const [bulk, setBulk] = useState({ classification: '', p53: '', stock: '' });
  const [search, setSearch] = useState('');
  const [newPlayer, setNewPlayer] = useState({ name: '', jersey: '', position: 'QB' });
  const [newTx, setNewTx] = useState({ player_name: '', position: '', type: 'SIGNED', note: '' });
  const [newBattle, setNewBattle] = useState({ title: '', description: '', players: '' });
  const [spots, setSpots] = useState({});

  const load = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/roster/tracker`);
      setData(response.data);
      setSpots(response.data.settings.position_spots || {});
    } catch (error) {
      logError('roster admin load failed:', error);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!data) return <p className="text-sm text-slate-500 p-4">Loading roster tracker…</p>;

  const act = async (fn, okMsg) => {
    try {
      await fn();
      toast.success(okMsg);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    }
  };

  const CUTS = ['WAIVED', 'RELEASED', 'WAIVED_INJURED', 'RETIRED', 'TRADED'];
  const inP53 = (p) => p.p53_explicit ? !!p.in_projected_53 : (['LOCK','LIKELY_IN','MADE_53'].includes(p.classification) || !!p.in_projected_53);
  const matchClass = (p) => {
    if (classFilter === 'ALL') return true;
    if (classFilter === 'UNCLASSIFIED') return !p.classification;
    if (classFilter === 'P53') return inP53(p);
    if (classFilter === 'NOT_P53') return !inP53(p);
    if (classFilter === 'CUT') return CUTS.includes(p.official_status) || p.classification === 'RELEASED';
    if (classFilter === 'INJ') return ['INJURED_RESERVE','PUP','NFI','WAIVED_INJURED'].includes(p.official_status) || p.classification === 'INJURED';
    return p.classification === classFilter;
  };
  const players = data.players.filter(
    (p) => (posFilter === 'ALL' || p.position === posFilter) && matchClass(p) &&
      (!search || p.name.toLowerCase().includes(search.toLowerCase()))
  );
  const cnt = (fn) => data.players.filter(fn).length;
  const bulkApply = async () => {
    const payload = {};
    if (bulk.classification) payload.classification = bulk.classification;
    if (bulk.p53) payload.p53_override = bulk.p53;
    if (bulk.stock) payload.stock = bulk.stock;
    if (!Object.keys(payload).length || !selected.length) return;
    try {
      await Promise.all(selected.map((id) => axios.put(`${API}/roster/players/${id}`, payload)));
      toast.success(`Updated ${selected.length} players`);
      setSelected([]); setBulk({ classification: '', p53: '', stock: '' });
      load();
    } catch (e) { toast.error('Bulk update failed'); }
  };

  return (
    <div data-testid="roster-admin" className="space-y-6">
      {/* Controls */}
      <Card className="border border-slate-200">
        <CardHeader><CardTitle className="font-heading uppercase text-lg">Road to 53 — Controls</CardTitle></CardHeader>
        <CardContent className="flex flex-wrap items-center gap-3">
          <Button data-testid="roster-import-btn" onClick={() => act(() => axios.post(`${API}/roster/players/import`), 'Roster imported')} className="rounded-none bg-[#5A1414]">
            <Download className="w-4 h-4 mr-2" /> Import Official Roster
          </Button>
          <label className="text-sm text-slate-600 flex items-center gap-2">
            Phase:
            <select
              data-testid="roster-phase-select"
              value={data.settings.phase}
              onChange={(e) => act(() => axios.put(`${API}/roster/settings`, { phase: e.target.value }), 'Phase updated')}
              className={selectCls}
            >
              <option value="tracking">Road to 53 (tracking)</option>
              <option value="final">Final 53-Man Roster (archive)</option>
            </select>
          </label>
          <span className="text-xs text-slate-500">
            {data.counts.active} active · {data.counts.decisions_remaining} decisions remaining · Projected {data.counts.projected}/53
            {data.counts.projected !== 53 && <span className="ml-2 text-amber-700 font-bold uppercase">⚠ Projected 53 needs editor review</span>}
          </span>
        </CardContent>
      </Card>

      {/* Position spots */}
      <Card className="border border-slate-200">
        <CardHeader><CardTitle className="font-heading uppercase text-lg">Projected Spots by Position</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
            {data.positions.map((p) => (
              <label key={p} className="text-xs text-slate-600">
                {p}
                <input
                  type="number" min="0" max="15"
                  value={spots[p] ?? 0}
                  onChange={(e) => setSpots((s) => ({ ...s, [p]: parseInt(e.target.value || '0', 10) }))}
                  className={inputCls}
                />
              </label>
            ))}
          </div>
          <Button
            data-testid="roster-spots-save"
            onClick={() => act(() => axios.put(`${API}/roster/settings`, { position_spots: spots }), 'Projected spots saved')}
            className="mt-3 rounded-none bg-[#002868]" size="sm"
          >
            Save Spots
          </Button>
        </CardContent>
      </Card>

      {/* Transactions */}
      <Card className="border border-slate-200">
        <CardHeader><CardTitle className="font-heading uppercase text-lg">Official Roster Moves</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <Input data-testid="tx-player-input" value={newTx.player_name} onChange={(e) => setNewTx({ ...newTx, player_name: e.target.value })} placeholder="Player name" className="h-8 w-44 text-xs" />
            <Input value={newTx.position} onChange={(e) => setNewTx({ ...newTx, position: e.target.value })} placeholder="Pos" className="h-8 w-16 text-xs" />
            <select value={newTx.type} onChange={(e) => setNewTx({ ...newTx, type: e.target.value })} className={selectCls}>
              {data.transaction_types.map((t) => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
            </select>
            <Input value={newTx.note} onChange={(e) => setNewTx({ ...newTx, note: e.target.value })} placeholder="Note (optional)" className="h-8 flex-1 min-w-[140px] text-xs" />
            <Button
              size="sm" data-testid="tx-add-btn"
              disabled={!newTx.player_name.trim()}
              onClick={() => act(() => axios.post(`${API}/roster/transactions`, newTx).then(() => setNewTx({ player_name: '', position: '', type: 'SIGNED', note: '' })), 'Transaction added')}
              className="rounded-none bg-[#002868] h-8"
            >
              <Plus className="w-3 h-3 mr-1" /> Add
            </Button>
          </div>
          <div className="divide-y divide-slate-100">
            {data.transactions.map((tx) => (
              <div key={tx.id} className="flex items-center justify-between py-1.5 text-sm">
                <span><b>{tx.player_name}</b> — {tx.type.replace(/_/g, ' ')} {tx.note && <span className="text-slate-500 text-xs">· {tx.note}</span>}</span>
                <button onClick={() => act(() => axios.delete(`${API}/roster/transactions/${tx.id}`), 'Deleted')} className="text-red-600 text-xs">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Battles */}
      <Card className="border border-slate-200">
        <CardHeader><CardTitle className="font-heading uppercase text-lg">Position Battles</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <Input data-testid="battle-title-input" value={newBattle.title} onChange={(e) => setNewBattle({ ...newBattle, title: e.target.value })} placeholder="Battle title (e.g., WR5 / WR6)" className="h-8 w-52 text-xs" />
            <Input value={newBattle.players} onChange={(e) => setNewBattle({ ...newBattle, players: e.target.value })} placeholder="Players (comma separated)" className="h-8 flex-1 min-w-[160px] text-xs" />
            <Input value={newBattle.description} onChange={(e) => setNewBattle({ ...newBattle, description: e.target.value })} placeholder="Editorial description" className="h-8 flex-1 min-w-[160px] text-xs" />
            <Button
              size="sm" data-testid="battle-add-btn"
              disabled={!newBattle.title.trim()}
              onClick={() => act(() => axios.post(`${API}/roster/battles`, {
                title: newBattle.title,
                description: newBattle.description,
                players: newBattle.players.split(',').map((s) => s.trim()).filter(Boolean),
              }).then(() => setNewBattle({ title: '', description: '', players: '' })), 'Battle added')}
              className="rounded-none bg-[#002868] h-8"
            >
              <Plus className="w-3 h-3 mr-1" /> Add
            </Button>
          </div>
          <div className="divide-y divide-slate-100">
            {data.battles.map((b) => (
              <div key={b.id} className="flex items-center justify-between py-1.5 text-sm">
                <span><b>{b.title}</b> {b.players?.length > 0 && <span className="text-slate-500 text-xs">· {b.players.join(', ')}</span>}</span>
                <button onClick={() => act(() => axios.delete(`${API}/roster/battles/${b.id}`), 'Deleted')} className="text-red-600 text-xs">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Live counts */}
      <div data-testid="roster-admin-counts" className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-2 text-center">
        {[['Roster', data.counts.active], ['Unclassified', cnt((p) => !p.classification)], ['Locks', cnt((p) => p.classification === 'LOCK')], ['Likely In', cnt((p) => p.classification === 'LIKELY_IN')], ['Bubble', cnt((p) => p.classification === 'BUBBLE')], ['Long Shots', cnt((p) => p.classification === 'LONG_SHOT')], ['Proj. Out', cnt((p) => p.classification === 'PROJECTED_OUT')], [`Projected`, `${cnt(inP53)}/53`], ['Cut', cnt((p) => CUTS.includes(p.official_status))]].map(([l, v]) => (
          <div key={l} className="bg-white border border-slate-200 py-2"><p className="font-heading text-lg text-slate-900">{v}</p><p className="text-[9px] font-bold uppercase text-slate-500">{l}</p></div>
        ))}
      </div>

      {/* Bulk toolbar */}
      <div data-testid="bulk-toolbar" className="bg-white border border-slate-200 p-3 flex flex-wrap items-center gap-2 text-xs">
        <b>{selected.length} selected</b>
        <Button size="sm" variant="outline" className="h-7 rounded-none text-xs" onClick={() => setSelected(players.map((p) => p.id))}>Select All Shown</Button>
        <Button size="sm" variant="outline" className="h-7 rounded-none text-xs" onClick={() => setSelected([])}>Clear</Button>
        <select value={bulk.classification} onChange={(e) => setBulk({ ...bulk, classification: e.target.value })} className={selectCls}>
          <option value="">Projection…</option>{CLASS_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={bulk.p53} onChange={(e) => setBulk({ ...bulk, p53: e.target.value })} className={selectCls}>
          <option value="">Projected 53…</option><option value="AUTO">Auto</option><option value="YES">Yes</option><option value="NO">No</option>
        </select>
        <select value={bulk.stock} onChange={(e) => setBulk({ ...bulk, stock: e.target.value })} className={selectCls}>
          <option value="">Stock…</option><option value="NONE">Clear</option><option value="UP">Up</option><option value="DOWN">Down</option>
        </select>
        <Button size="sm" data-testid="bulk-apply-btn" disabled={!selected.length} onClick={bulkApply} className="h-7 rounded-none bg-[#5A1414] text-xs">Apply to Selected</Button>
        <span className="text-slate-400">Tip: filter by position, then "Select All Shown" = select position group. Official status is never bulk-edited.</span>
      </div>

      {/* Players */}
      <Card className="border border-slate-200">
        <CardHeader><CardTitle className="font-heading uppercase text-lg">Players ({players.length})</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <Input data-testid="roster-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search player…" className="h-8 w-48 text-xs" />
            <select data-testid="admin-class-filter" value={classFilter} onChange={(e) => setClassFilter(e.target.value)} className={selectCls}>
              <option value="ALL">All Projections</option><option value="UNCLASSIFIED">Unclassified</option><option value="LOCK">Roster Lock</option><option value="LIKELY_IN">Likely In</option><option value="BUBBLE">On the Bubble</option><option value="LONG_SHOT">Long Shot</option><option value="PROJECTED_OUT">Projected Out</option><option value="P53">Projected 53</option><option value="NOT_P53">Not in Projected 53</option><option value="CUT">Official Cut/Released</option><option value="INJ">Injured/Reserve</option>
            </select>
            <select value={posFilter} onChange={(e) => setPosFilter(e.target.value)} className={selectCls}>
              <option value="ALL">All Positions</option>
              {data.positions.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <div className="flex gap-2 items-center ml-auto">
              <Input value={newPlayer.name} onChange={(e) => setNewPlayer({ ...newPlayer, name: e.target.value })} placeholder="New player name" className="h-8 w-40 text-xs" />
              <Input value={newPlayer.jersey} onChange={(e) => setNewPlayer({ ...newPlayer, jersey: e.target.value })} placeholder="#" className="h-8 w-12 text-xs" />
              <select value={newPlayer.position} onChange={(e) => setNewPlayer({ ...newPlayer, position: e.target.value })} className={selectCls}>
                {data.positions.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
              <Button
                size="sm" data-testid="roster-add-player-btn"
                disabled={!newPlayer.name.trim()}
                onClick={() => act(() => axios.post(`${API}/roster/players`, newPlayer).then(() => setNewPlayer({ name: '', jersey: '', position: 'QB' })), 'Player added')}
                className="rounded-none bg-[#5A1414] h-8"
              >
                <Plus className="w-3 h-3 mr-1" /> Add
              </Button>
            </div>
          </div>
          <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1">
            {players.map((p) => (
              <div key={p.id} className="flex items-start gap-2">
              <input type="checkbox" className="mt-4" checked={selected.includes(p.id)} onChange={(e) => setSelected(e.target.checked ? [...selected, p.id] : selected.filter((x) => x !== p.id))} />
              <div className="flex-1"><PlayerAdminRow
                key={`${p.id}-${p.classification}-${p.official_status}-${p.stock}-${p.note}-${p.in_projected_53}-${p.exempt}`}
                player={p}
                positions={data.positions}
                onSave={(id, draft) => act(() => axios.put(`${API}/roster/players/${id}`, {
                  name: draft.name, jersey: draft.jersey, position: draft.position,
                  classification: draft.classification || 'NONE', note: draft.note,
                  official_status: draft.official_status || 'NONE',
                  p53_override: draft.p53_explicit ? (draft.in_projected_53 ? 'YES' : 'NO') : 'AUTO',
                  stock: draft.stock || 'NONE', stock_note: draft.stock_note,
                  in_projected_53: draft.in_projected_53, exempt: draft.exempt,
                }), 'Player updated')}
                onDelete={(id) => window.confirm('Remove this player from the tracker?') && act(() => axios.delete(`${API}/roster/players/${id}`), 'Player removed')}
              /></div></div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
